import { useState } from "react";
import { Check } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { fcfa, fmtDate } from "@/lib/api";
import { Loading } from "@/components/RequireAuth";
import { StatusBadge } from "@/components/Badges";
import { PlanCard } from "@/pages/Pricing";
import { Button } from "@/components/ui/button";
import { PaymentForm, LocalEquivalent, METHODS } from "@/pages/seller/SubscriptionPayment";

const STEPS = [["pending", "En attente de paiement", "created_at"], ["payment_verified", "Paiement vérifié", "verified_at"], ["active", "Activé", "activated_at"]];

const SubTimeline = ({ s }) => {
  if (s.status === "rejected") return <p data-testid={`sub-timeline-${s.id}`} className="text-sm text-red-700">Demande refusée{s.note ? ` : ${s.note}` : ""}.</p>;
  if (s.kind === "offert") return <p data-testid={`sub-timeline-${s.id}`} className="text-sm text-violet-900">Accès offert par Casadeal (aucun paiement) · {s.status === "revoked" ? "retiré" : s.status === "expired" ? "terminé" : `jusqu'au ${fmtDate(s.end_date)}`}.</p>;
  const reached = s.status === "expired" ? 3 : STEPS.findIndex(([k]) => k === s.status) + 1;
  return (
    <ol data-testid={`sub-timeline-${s.id}`} className="grid grid-cols-3 gap-2">
      {STEPS.map(([k, label, dateKey], i) => (
        <li key={k} data-testid={`sub-step-${s.id}-${k}`} data-done={i < reached} className={`rounded-lg px-2 py-2 text-center text-xs font-semibold ${i < reached ? "bg-green-700 text-white" : "bg-gray-100 text-gray-500"}`}>
          <span className="flex items-center justify-center gap-1">{i < reached && <Check className="h-3 w-3" />}{label}</span>
          {i < reached && s[dateKey] && <span className="block text-[10px] font-normal opacity-80">{fmtDate(s[dateKey])}</span>}
        </li>
      ))}
    </ol>
  );
};

export default function SellerSubscription() {
  const { data, reload } = useApi("/seller/subscriptions");
  const { data: plans } = useApi("/plans");
  const [selected, setSelected] = useState(null);
  if (!data || !plans) return <Loading />;
  const pending = data.subscriptions.some((s) => ["pending", "payment_verified"].includes(s.status));
  const approved = data.shop_status === "approved";
  const open = data.country_open;
  const label = !approved ? "Disponible après validation de votre boutique" : !open ? "Vente pas encore ouverte dans votre pays" : pending ? "Demande en attente" : "Choisir cette formule";
  return (
    <div data-testid="seller-subscription" className="space-y-6">
      <h1 className="font-display text-3xl font-black tracking-tight">Abonnement</h1>
      <div className="rounded-xl border bg-white p-4 text-sm">
        {data.current_end ? (
          <p data-testid="current-subscription">Formule actuelle : <b>{data.current_plan === "premium" ? "Premium" : "Économie"}</b> · fin le <b>{fmtDate(data.current_end)}</b> · {data.visible ? "boutique visible" : "boutique non visible"}</p>
        ) : <p data-testid="current-subscription">Aucun abonnement actif.</p>}
        <p className="mt-2 text-gray-500">Choisissez une formule, payez par Wave ou Orange Money en suivant les instructions, puis indiquez la référence de la transaction. L'administratrice vérifie le paiement et active l'abonnement manuellement.</p>
        {!approved && <p data-testid="subscription-locked-notice" className="mt-3 rounded-lg bg-amber-50 p-3 font-semibold text-amber-900 ring-1 ring-amber-200">Disponible après validation de votre boutique. Vous pouvez déjà préparer vos produits : ils resteront invisibles jusqu'à la validation et l'activation de votre abonnement.</p>}
        {approved && !open && <p data-testid="subscription-country-closed-notice" className="mt-3 rounded-lg bg-amber-50 p-3 font-semibold text-amber-900 ring-1 ring-amber-200">La vente n'est pas encore ouverte dans votre pays ({data.country_name}). Les abonnements seront disponibles dès son ouverture par Casadeal. Vous pouvez déjà préparer vos produits.</p>}
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {plans.map((p) => (
          <PlanCard key={p.code} plan={p} action={
            <div className="space-y-2">
              <LocalEquivalent price={p.price} local={data.local_currency} />
              <Button className="w-full rounded-full" variant={p.code === "premium" ? "default" : "outline"} disabled={!approved || !open || pending} onClick={() => setSelected(p)} data-testid={`request-plan-${p.code}`}>{label}</Button>
            </div>
          } />
        ))}
      </div>
      {selected && approved && open && !pending && <PaymentForm plan={selected} local={data.local_currency} onCancel={() => setSelected(null)} onDone={() => { setSelected(null); reload(); }} />}
      <div className="rounded-xl border bg-white">
        <p className="border-b p-4 font-display font-bold">Historique</p>
        {data.subscriptions.length === 0 ? <p className="p-4 text-sm text-gray-500">Aucune demande.</p> : data.subscriptions.map((s) => (
          <div key={s.id} data-testid={`sub-row-${s.id}`} className="space-y-3 border-b p-4 text-sm last:border-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">{s.plan === "premium" ? "Premium" : "Économie"} · {s.kind === "offert" ? <span data-testid={`sub-offer-${s.id}`} className="text-violet-800">Accès offert — 0 FCFA</span> : fcfa(s.price)}</span>
            <span className="text-gray-500">{s.start_date ? `${fmtDate(s.start_date)} → ${fmtDate(s.end_date)}` : `Demandé le ${fmtDate(s.created_at)}`}</span>
            <StatusBadge status={s.status} kind="sub" testid={`sub-status-${s.id}`} />
            </div>
            <SubTimeline s={s} />
            {s.declared_reference && <p data-testid={`sub-declared-${s.id}`} className="text-xs text-gray-600">{METHODS[s.payment_method] || s.payment_method} · référence déclarée : <b>{s.declared_reference}</b></p>}
            {s.status === "pending" && <p className="text-xs text-gray-500">Vérification manuelle avant activation : votre déclaration seule n'active pas l'abonnement.</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
